import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CustomerSearchCombobox } from "@/components/shared/customer-search-combobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import {
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  Activity,
  MessageSquare,
  Save,
  X,
  History,
  Clock,
  ArrowRightLeft,
  ArrowDownToLine,
  Search,
} from "lucide-react";
import { getCurrencySymbol } from "@/lib/utils";
import { useCompany } from "@/contexts/CompanyContext";
import { toast } from "sonner";
import { useCompanyUsersList } from "@/hooks/useUsersData";
import { useUserContext } from "@/hooks/useUserContext";
import { type Transaction as TransactionFull, type ColumnVisibility } from "@/types/transaction";
import { formatCurrency } from "@/lib/utils/formatters";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { TransactionTable } from "@/components/transactions/transaction-table";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { useStrategiesList } from "@/hooks/useStrategiesData";
import {
  useCreatePromiseTransactionsPaginatedGraphQL,
  buildCreatePromiseTransactionExportFilter,
  CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP,
} from "@/hooks/useCreatePromiseTransactionsGraphQL";
import { useCreateCollectionCase, type CreateCollectionCaseRequest } from "@/hooks/useCollectionCasesData";
import { useCreatePromiseContacts, type CreatePromiseFormCustomer } from "@/hooks/useCreatePromiseData";
import type { ContactApi } from "@/types/api/customer-detail";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useExportAll } from "@/hooks/useExportAll";
import { Forms } from "@/types/export";
import type { ColumnInput } from "@/types/export";
import { ExportProvider } from "@/providers/ExportProvider";

interface NewCaseData {
  customerName: string;
  customerAccountNumber: string;
  customerEmail: string;
  customerPhone: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Active' | 'On Hold' | 'Closed' | 'Escalated';
  strategyId: string;
  assignedTo?: string;
  notes: string;
  nextAction: string;
  nextActionDate: string;
}

function getContactPlaceholder(
  selectedCustomer: CreatePromiseFormCustomer | null,
  contactCount: number,
): string {
  if (!selectedCustomer) return 'Select a customer first';
  if (contactCount === 0) return 'No contacts available';
  return 'Select a contact';
}

const DEFAULT_CASE_TRANSACTION_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0, locked: true },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'transactionType', label: 'Type', visible: true, order: 3 },
  { id: 'amount', label: 'Gross', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: true, order: 5 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 6 },
  { id: 'status', label: 'Status', visible: true, order: 7 },
  { id: 'description', label: 'Description', visible: false, order: 8 },
  { id: 'accountCode', label: 'Account', visible: false, order: 9 },
  { id: 'netAmount', label: 'Net', visible: false, order: 10 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 11 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 12 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 13 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 14 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 15 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 16 },
  { id: 'flags', label: 'Flags', visible: true, order: 17 },
  { id: 'onDispute', label: 'External Dispute', visible: false, order: 18 },
  { id: 'actions', label: 'Actions', visible: true, order: 19 },
];

export default function CreateCasePage() {
  return (
    <ExportProvider>
      <CreateCasePageInner />
    </ExportProvider>
  );
}

function CreateCasePageInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const prePopulatedCustomer = location.state?.prePopulatedCustomer;
  const { selectedCompany } = useCompany();
  const currencySymbol = getCurrencySymbol(selectedCompany?.currency || "GBP");

  const [activeTab, setActiveTab] = useState("overview");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [transactionSearch, setTransactionSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>('transactionDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txItemsPerPage, setTxItemsPerPage] = useState(25);
  const [selectedCustomer, setSelectedCustomer] = useState<CreatePromiseFormCustomer | null>(null);
  const [availableContacts, setAvailableContacts] = useState<ContactApi[]>([]);
  const [selectedContact, setSelectedContact] = useState<ContactApi | null>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [transactionDetailSheetOpen, setTransactionDetailSheetOpen] = useState(false);
  const [selectedTransactionDetail, setSelectedTransactionDetail] = useState<TransactionFull | null>(null);
  const createCaseMutation = useCreateCollectionCase();

  // Fetch active users for the Assigned To dropdown (company-scoped)
  const { data: users = [] } = useCompanyUsersList();
  const activeUsers = (users || []).filter(user => user?.status === 'active');

  // Get logged-in user context to default the Assigned To field
  const { data: userContext } = useUserContext();
  
  const strategiesQuery = useStrategiesList();
  const availableStrategies = useMemo(() =>
    (strategiesQuery.data ?? []).map(s => ({
      id: s.id,
      name: s.name,
      type: s.type,
    })),
    [strategiesQuery.data]
  );

  // Form data state
  const [formData, setFormData] = useState<NewCaseData>({
    customerName: prePopulatedCustomer?.customerName || "",
    customerAccountNumber: prePopulatedCustomer?.accountNumber || "",
    customerEmail: "",
    customerPhone: "",
    priority: "Medium",
    status: "Active",
    strategyId: "",
    assignedTo: undefined,
    notes: "",
    nextAction: "Follow-up call",
    nextActionDate: format(new Date(), "yyyy-MM-dd"),
  });

  // Default Assigned To to the logged-in user
  useEffect(() => {
    if (!userContext?.internalUserId || activeUsers.length === 0) return;
    const loggedInUserId = String(userContext.internalUserId);
    const userExists = activeUsers.some(u => u.id === loggedInUserId);
    if (userExists && !formData.assignedTo) {
      setFormData(prev => ({ ...prev, assignedTo: loggedInUserId }));
    }
  }, [userContext?.internalUserId, activeUsers, formData.assignedTo]);

  const selectedCustomerInternalId = selectedCustomer?.id;

  // Server-side GraphQL transactions using CreatePromise endpoint (same filtering: outstanding != 0, not receipts)
  const {
    transactions: graphqlTransactions,
    pagination: txPagination,
  } = useCreatePromiseTransactionsPaginatedGraphQL(
    selectedCustomerInternalId || undefined,
    {
      page: txCurrentPage,
      pageSize: txItemsPerPage,
      search: transactionSearch || undefined,
      sortBy,
      sortOrder,
    },
  );
  const availableTransactions: TransactionFull[] = graphqlTransactions;

  // Column configuration
  const {
    columns: caseTxCols,
    savePreferences: saveCaseTxPrefs,
    resetToDefaults: resetCaseTxPrefs,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.CreateCaseTransactions, DEFAULT_CASE_TRANSACTION_COLUMNS);
  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    caseTxCols.forEach((col) => { vis[col.id as keyof ColumnVisibility] = col.visible; });
    return vis;
  }, [caseTxCols]);

  const { contacts: apiContacts } = useCreatePromiseContacts(selectedCustomerInternalId);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = 
      formData.customerEmail.trim() !== "" ||
      formData.customerPhone.trim() !== "" ||
      formData.notes.trim() !== "" ||
      selectedTransactions.size > 0 ||
      formData.priority !== "Medium" ||
      formData.status !== "Active" ||
      formData.strategyId !== "" ||
      formData.assignedTo !== undefined ||
      formData.nextAction !== "Follow-up call";
    
    setHasUnsavedChanges(prev => prev === hasChanges ? prev : hasChanges);
  }, [formData, selectedTransactions]);

  // Set selectedCustomer from pre-populated customer
  useEffect(() => {
    if (prePopulatedCustomer) {
      setSelectedCustomer({
        id: prePopulatedCustomer.customerId ?? '',
        name: prePopulatedCustomer.customerName ?? '',
        accountNumber: prePopulatedCustomer.accountNumber ?? '',
        email: '',
        phone: '',
      });
      setFormData(prev => ({
        ...prev,
        customerAccountNumber: prePopulatedCustomer.accountNumber ?? '',
        customerName: prePopulatedCustomer.customerName ?? '',
      }));
    }
  }, [prePopulatedCustomer]);

  // Load contacts when customer is selected or API contacts arrive
  useEffect(() => {
    if (!selectedCustomer) {
      setAvailableContacts([]);
      setSelectedContact(null);
      return;
    }
    if (apiContacts.length === 0) return;

    setAvailableContacts(apiContacts);
    const primaryContact = apiContacts.find(x => x.isPrimary);
    if (primaryContact) {
      setSelectedContact(primaryContact);
      setFormData(prev => ({
        ...prev,
        customerEmail: primaryContact.email,
        customerPhone: primaryContact.phone,
      }));
    }
  }, [selectedCustomer, apiContacts]);

  // Handle customer selection from combobox
  const handleCustomerSelect = (customer: CreatePromiseFormCustomer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customerAccountNumber: customer.accountNumber,
      customerName: customer.name,
      customerEmail: '',
      customerPhone: '',
    }));
    setSelectedTransactions(new Set());
    setSelectedContact(null);
    setAvailableContacts([]);
    setTxCurrentPage(1);
    setHasUnsavedChanges(true);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true);
      return;
    }
    
    navigate("/collections/queue");
  };

  // Confirm close without saving
  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    setHasUnsavedChanges(false);
    
    navigate("/collections/queue");
  };

  const handleContactChange = (contactId: string) => {
    const contact = availableContacts.find(c => c.id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setFormData(prev => ({
        ...prev,
        customerEmail: contact.email,
        customerPhone: contact.phone,
      }));
    }
  };

  const handleViewTransactionDetail = useCallback((transaction: TransactionFull) => {
    setSelectedTransactionDetail(transaction);
    setTransactionDetailSheetOpen(true);
  }, []);

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveCaseTxPrefs(columns);
  };

  // Derive the selected currency from the currently selected transactions
  const selectedCurrency = useMemo(() => {
    for (const txId of Array.from(selectedTransactions)) {
      const tx = availableTransactions.find(x => String(x.id) === txId);
      if (tx?.currency) return tx.currency;
    }
    return undefined;
  }, [selectedTransactions, availableTransactions]);

  // Currency-aware single row toggle
  const handleToggleTransaction = useCallback((id: number, checked: boolean) => {
    const txId = String(id);
    if (checked) {
      const tx = availableTransactions.find(x => String(x.id) === txId);
      if (tx?.currency && selectedCurrency && tx.currency !== selectedCurrency) {
        toast.error(`Cannot mix currencies. Selected transactions use ${selectedCurrency} but the selected transaction is in ${tx.currency}.`);
        return;
      }
    }
    const newSelected = new Set(selectedTransactions);
    if (checked) {
      newSelected.add(txId);
    } else {
      newSelected.delete(txId);
    }
    setSelectedTransactions(newSelected);
  }, [availableTransactions, selectedTransactions, selectedCurrency]);

  const handleSelectAllTransactions = useCallback(() => {
    const baseCurrency = selectedCurrency
      ?? availableTransactions.find(x => x.currency)?.currency;
    const allIds = new Set(selectedTransactions);
    let skipped = false;
    for (const tx of availableTransactions) {
      if (baseCurrency && tx.currency && tx.currency !== baseCurrency) {
        skipped = true;
        continue;
      }
      allIds.add(String(tx.id));
    }
    setSelectedTransactions(allIds);
    if (skipped) {
      toast.error(`Some transactions were skipped because they use a different currency than the already selected transactions (${baseCurrency}).`);
    }
  }, [availableTransactions, selectedTransactions, selectedCurrency]);

  const handleDeselectAllTransactions = useCallback(() => {
    const currentPageIds = new Set(availableTransactions.map(x => String(x.id)));
    setSelectedTransactions(prev => {
      const newSelected = new Set(prev);
      currentPageIds.forEach(id => newSelected.delete(id));
      return newSelected;
    });
  }, [availableTransactions]);

  const handleToggleSelectAllTransactions = useCallback((checked: boolean) => {
    const action = checked ? handleSelectAllTransactions : handleDeselectAllTransactions;
    action();
  }, [handleSelectAllTransactions, handleDeselectAllTransactions]);

  // Server-side sort change handler
  const onSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setTxCurrentPage(1);
  }, []);

  // Server-side XLSX export — build columns and filter
  const getExportFilter = useCallback((): Record<string, unknown> | undefined => {
    return buildCreatePromiseTransactionExportFilter(selectedCustomerInternalId || undefined, transactionSearch || undefined);
  }, [selectedCustomerInternalId, transactionSearch]);

  const getExportSummary = useCallback((): string | undefined => {
    return transactionSearch || undefined;
  }, [transactionSearch]);

  const getVisibleExportColumns = useCallback((): ColumnInput[] => {
    return caseTxCols
      .filter((col) => col.visible && col.id in CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP)
      .map((col) => ({
        name: CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, [caseTxCols]);

  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_CASE_TRANSACTION_COLUMNS
      .filter((col) => col.id in CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP)
      .map((col) => ({
        name: CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, []);

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: Forms.CreatePromiseTransactions,
    getColumns: getVisibleExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `case-transactions-${selectedCustomer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: Forms.CreatePromiseTransactions,
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `case-transactions-all-columns-${selectedCustomer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportCurrentView = useCallback(() => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportCurrentView]);

  const handleExportAllColumns = useCallback(() => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportAllColumns]);

  const validateForm = (): boolean => {
    // Required fields validation
    if (!formData.customerAccountNumber.trim()) {
      toast.error("Please select a customer");
      setActiveTab("overview");
      return false;
    }

    // At least one transaction must be selected - check early before other fields
    if (selectedTransactions.size === 0) {
      toast.error("Please select at least one transaction");
      setActiveTab("overview");
      return false;
    }

    // Currency consistency validation
    const selectedTxs = availableTransactions.filter(x => selectedTransactions.has(String(x.id)));
    const currencies = [...new Set(selectedTxs.map(x => x.currency).filter(Boolean))];
    if (currencies.length > 1) {
      toast.error(`All transactions must share the same currency. Found: ${currencies.join(', ')}`);
      setActiveTab("overview");
      return false;
    }

    // Email format validation (only if email is provided)
    if (formData.customerEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.customerEmail)) {
        toast.error("Please enter a valid email address");
        setActiveTab("overview");
        return false;
      }
    }

    if (!formData.nextAction.trim()) {
      toast.error("Next action is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextActionDate) {
      toast.error("Next action date is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.assignedTo) {
      toast.error("Assigned To is required");
      setActiveTab("overview");
      return false;
    }

    return true;
  };

  const handleSaveCase = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const totalAmount = totalSelectedOutstanding;

      const priorityMap: Record<string, number> = { High: 1, Medium: 2, Low: 3 };

      const selectedTransactionIds = Array.from(selectedTransactions).map(Number);

      const selectedTxsForSave = availableTransactions.filter(x => selectedTransactions.has(String(x.id)));
      const currenciesForSave = [...new Set(selectedTxsForSave.map(x => x.currency).filter(Boolean))];
      const currencyCode = currenciesForSave.length === 1 ? currenciesForSave[0] : undefined;

      const request: CreateCollectionCaseRequest = {
        customerId: selectedCustomerInternalId ? parseInt(selectedCustomerInternalId, 10) : 0,
        priorityId: priorityMap[formData.priority] ?? 2,
        strategyId: formData.strategyId ? parseInt(formData.strategyId, 10) : undefined,
        amount: totalAmount,
        nextAction: formData.nextAction || undefined,
        nextActionDate: formData.nextActionDate || undefined,
        contactId: selectedContact ? Number(selectedContact.id) : undefined,
        notes: formData.notes || undefined,
        transactionIds: selectedTransactionIds.length > 0 ? selectedTransactionIds : undefined,
        assignedToUserId: Number(formData.assignedTo),
        currencyCode,
      };

      await createCaseMutation.mutateAsync(request);

      const transactionCount = selectedTransactionIds.length;
      toast.success(
        transactionCount > 0
          ? `Collection case created successfully with ${transactionCount} transaction(s) linked`
          : "Collection case created successfully"
      );

      setHasUnsavedChanges(false);
      navigate("/collections/queue");
    } catch (error) {
      toast.error("Failed to create case. Please try again.");
      console.error("Error creating case:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Total Outstanding from selected transactions only (uses DisplayOutstandingAmount)
  const totalSelectedOutstanding = useMemo(() => {
    return availableTransactions
      .filter(x => selectedTransactions.has(String(x.id)))
      .reduce((sum, x) => sum + (x.displayOutstandingAmount ?? x.outstandingAmount ?? x.amount), 0);
  }, [availableTransactions, selectedTransactions]);

  return (
    <div className="flex-1 space-y-6 p-6 pt-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Create New Case</h1>
          <p className="text-muted-foreground">
            Enter customer and case information to create a new collection case
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="default"
            onClick={handleBack}
            disabled={isSaving}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            size="default"
            onClick={handleSaveCase}
            disabled={isSaving}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {isSaving ? "Creating..." : "Create Case"}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Two Column Layout - Customer & Case Info */}
          <div className="grid gap-6 md:grid-cols-[1fr_2fr]">
            {/* Customer Information & Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  Customer Information
                </CardTitle>
                <CardDescription>Contact details and customer profile</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                  {/* First Row: Customer and Contact Name */}
                  <div className="grid grid-cols-2 gap-x-4">
                    {/* Customer Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        Customer *
                      </Label>
                      {prePopulatedCustomer ? (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold">[{formData.customerAccountNumber}] {formData.customerName}</p>
                          <Badge variant="secondary" className="text-xs">Pre-selected</Badge>
                        </div>
                      ) : (
                        <CustomerSearchCombobox
                          selectedCustomer={selectedCustomer}
                          onSelectCustomer={handleCustomerSelect}
                        />
                      )}
                    </div>

                    {/* Contact Name Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Contact Name
                      </Label>
                      <Select
                        value={selectedContact?.id ?? ''}
                        onValueChange={handleContactChange}
                        disabled={!selectedCustomer || availableContacts.length === 0}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder={getContactPlaceholder(selectedCustomer, availableContacts.length)} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableContacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.firstName} {contact.surname}
                              {contact.isPrimary && " (Primary)"}
                              {contact.jobTitle && ` - ${contact.jobTitle}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Second Row: Email Address and Phone Number */}
                  <div className="grid grid-cols-2 gap-x-4">
                    {/* Email Address (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Email Address
                      </Label>
                      <Input
                        value={formData.customerEmail}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view email"
                      />
                    </div>

                    {/* Phone Number (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        Phone Number
                      </Label>
                      <Input
                        value={formData.customerPhone}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view phone"
                      />
                    </div>
                  </div>
              </CardContent>
            </Card>

            {/* Case Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  Case Management
                </CardTitle>
                <CardDescription>Case details and configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* First Row: Case Status, Priority Level, Assigned To */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="status" className="text-xs font-medium text-muted-foreground">
                      Case Status
                    </Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value: 'Active' | 'On Hold' | 'Closed' | 'Escalated') => 
                        setFormData(prev => ({ ...prev, status: value }))
                      }
                    >
                      <SelectTrigger id="status" className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Escalated">Escalated</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground">
                      Priority Level
                    </Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                        setFormData(prev => ({ ...prev, priority: value }))
                      }
                    >
                      <SelectTrigger id="priority" className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="assignedTo" className="text-xs font-medium text-muted-foreground">
                      Assigned To <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.assignedTo || ""}
                      onValueChange={(value) => 
                        setFormData(prev => ({ ...prev, assignedTo: value }))
                      }
                    >
                      <SelectTrigger id="assignedTo" className="h-8">
                        <SelectValue placeholder="Select user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {activeUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                <AvatarFallback className="text-[10px]">
                                  {user.firstName[0]}{user.lastName[0]}
                                </AvatarFallback>
                              </Avatar>
                              <span>{user.firstName} {user.lastName}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                {/* Second Row: Outstanding Amount, Next Action, Due Date */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Outstanding Amount</p>
                    <p className="text-base font-bold text-red-600 dark:text-red-400">
                      {currencySymbol}{totalSelectedOutstanding.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">From selected transactions</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="nextAction" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Next Action <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.nextAction}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, nextAction: value }))}
                    >
                      <SelectTrigger id="nextAction" className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                        <SelectItem value="Send email">Send email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="nextActionDate" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Due Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="nextActionDate"
                      type="date"
                      value={formData.nextActionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, nextActionDate: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notes Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                Case Notes
              </CardTitle>
              <CardDescription>Add notes about this case</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes about this case..."
                rows={4}
                className="resize-none"
              />
            </CardContent>
          </Card>

          {/* Transactions Section */}
          <Card className="min-w-0">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <CardTitle className="text-lg">Transactions</CardTitle>
                  {selectedCustomer && (
                    <Badge variant="secondary" className="ml-2">
                      {txPagination.totalItems}
                    </Badge>
                  )}
                </div>
                {selectedCustomer && (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="search"
                        placeholder="Quick search..."
                        value={transactionSearch}
                        onChange={(e) => { setTransactionSearch(e.target.value); setTxCurrentPage(1); }}
                        className="pl-8 h-9 w-[200px]"
                      />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          title="Export to Excel"
                        >
                          <ArrowDownToLine className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={handleExportCurrentView}>
                          Export Current View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleExportAllColumns}>
                          Export All Columns
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <ColumnConfigDialog
                      columns={caseTxCols}
                      onApply={handleColumnConfigApply}
                      onReset={resetCaseTxPrefs}
                    />
                  </div>
                )}
              </div>
              {!selectedCustomer && (
                <CardDescription>
                  Select a customer first to view transactions
                </CardDescription>
              )}
            </CardHeader>
            {selectedCustomer && (
            <CardContent className="overflow-x-auto">
              <TransactionTable
                transactions={availableTransactions}
                columnVisibility={columnVisibility}
                orderedColumns={caseTxCols}
                onViewTransaction={handleViewTransactionDetail}
                showCustomerColumn={false}
                displayAmounts={true}
                enableSelection={true}
                selectedRows={new Set(Array.from(selectedTransactions).map(x => Number(x)))}
                onSelectRow={handleToggleTransaction}
                onSelectAll={handleToggleSelectAllTransactions}
                currentPage={txCurrentPage}
                pageSize={txItemsPerPage}
                totalItems={txPagination.totalItems}
                onPageChange={setTxCurrentPage}
                onPageSizeChange={(size) => { setTxItemsPerPage(size); setTxCurrentPage(1); }}
                onSortChange={onSortChange}
                renderActions={(transaction) => (
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewTransactionDetail(transaction); }}>
                    View
                  </Button>
                )}
              />

              <Separator className="my-4" />
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Outstanding:</span>
                <span className="text-xl font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(totalSelectedOutstanding)}
                </span>
              </div>
            </CardContent>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
              <CardDescription>
                Activity history will appear here after the case is created
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No activity yet</p>
                <p className="text-xs mt-1">Activities will be logged once the case is created</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Audit Log
              </CardTitle>
              <CardDescription>
                Audit trail will be available after the case is created
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No audit entries yet</p>
                <p className="text-xs mt-1">All case modifications will be logged after creation</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog for Unsaved Changes */}
      <AlertDialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving? All your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transaction Detail Sheet */}
      <TransactionDetailSheet
        open={transactionDetailSheetOpen}
        onOpenChange={setTransactionDetailSheetOpen}
        transaction={selectedTransactionDetail}
      />
    </div>
  );
}
